import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { portfolioData } from '../data/mock';

const Hero = () => {
  const [current, setCurrent] = useState(0);
  const slides = portfolioData.hero.heroSlides;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <section id="home" data-testid="hero-section" className="relative h-screen w-full overflow-hidden bg-[#0A0A0A]">
      <AnimatePresence mode="wait">
        <motion.div
          key={current}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.05 }}
          transition={{ duration: 1.5, ease: [0.25, 1, 0.5, 1] }}
          className="absolute inset-0"
        >
          <img
            src={slides[current]}
            alt="Portfolio showcase"
            className="w-full h-full object-cover"
          />
        </motion.div>
      </AnimatePresence>

      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-black/50 to-black/20" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/60 to-transparent" />

      <div className="absolute inset-0 flex items-end pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.25, 1, 0.5, 1] }}
          >
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] font-medium text-white/60 mb-4" data-testid="hero-tagline">
              Photography & Cinematography
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-light tracking-tighter leading-none text-white" data-testid="hero-title">
              Nitin Sharma
            </h1>
            <p className="mt-6 text-base md:text-lg font-light tracking-wide text-white/50 max-w-lg">
              Capturing automotive elegance, timeless moments, and visual stories that endure.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-10 flex items-center gap-8"
          >
            <a
              href="#portfolio"
              data-testid="hero-cta-portfolio"
              className="group inline-flex items-center gap-3 text-sm uppercase tracking-[0.2em] font-medium text-white/80 hover:text-white transition-colors duration-300"
            >
              <span className="w-12 h-[1px] bg-white/40 group-hover:w-20 transition-all duration-500" />
              View Portfolio
            </a>
            <a
              href="#contact"
              data-testid="hero-cta-contact"
              className="text-sm uppercase tracking-[0.2em] font-medium text-white/40 hover:text-white/80 transition-colors duration-300"
            >
              Get in Touch
            </a>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <a href="#about" aria-label="Scroll down" data-testid="hero-scroll-indicator">
          <ChevronDown className="w-5 h-5 text-white/30 animate-bounce" />
        </a>
      </motion.div>
    </section>
  );
};

export default Hero;
